import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import styled from "styled-components";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons/faCircleInfo";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { GetLatestEntriesFilterDto } from "../../../model/entry/dto/GetLatestEntriesFilterDto";
import { MergeEntriesDto } from "../../../model/entry/dto/MergeEntriesDto";
import { Entry } from "../../../model/entry/Entry";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { entryActions } from "../../../redux/entry/entry.slice";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Tooltip } from "../../../utils/local-antd";
import { useDrawersContext } from "../../tasks/drawers-context";
import {
  getDuplicateEntryFieldDescriptors,
  getEntryFieldValue,
  withEntryFieldValue,
} from "../utils/duplicateEntryFields";
import {
  CardsGrid,
  GhostButton,
  scrollShadows,
  TooltipHost,
} from "./DuplicateCardPrimitives";
import { EntryDuplicateCard } from "./EntryDuplicateCard";
import { EntryDuplicatePreviewDrawer } from "./EntryDuplicatePreviewDrawer";
import { EntryKeepCard } from "./EntryKeepCard";
import { EntryKindBadge } from "./EntryKindBadge";
import { TestIDs } from "../../../tests/types/testIds";

const LeftOutBox = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xs};
  padding: ${Spacing.s} ${Spacing.m};
  border: 1px dashed ${({ theme }) => theme.darkSub};
  border-radius: ${Rounded.lg};
`;

const Intro = styled(Flex)`
  flex: none;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m} ${Spacing.l} 0;
`;

const Scroller = styled(Flex)`
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.m} ${Spacing.l};
  min-height: 0;
  overflow-y: auto;
  container-type: size;
  ${scrollShadows}
`;

const KeepDock = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  max-height: 50vh;
  padding: ${Spacing.m} ${Spacing.l};
  border-block-start: 1px solid ${({ theme }) => theme.darkSub};
  box-shadow: 0 -0.25rem 0.75rem rgba(0, 0, 0, 0.08);
  background-color: ${({ theme }) => theme.white};
`;

const getEntryLabel = (entry: Entry) =>
  entry.fields?.name || entry.fields?.email || entry.fields?.phone || "";

const byCreatedAt = (a: Entry, b: Entry) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

type ResolveFormValues = MergeEntriesDto & { finalEntry: Entry };

export const EntriesDuplicatesResolveDrawer: React.FC = () => {
  const [ignoreButtonLoading, setIgnoreButtonLoading] = useState(false);
  const [mergeButtonLoading, setMergeButtonLoading] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [autoPicked, setAutoPicked] = useState<{
    entryId: string;
    wasContact: boolean;
  } | null>(null);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const userPermissions = user.permissions.permissions;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const entriesPage = useAppSelector(
    entrySliceSelectors.selectEntriesTablePage,
  );
  const entriesPageSize = useAppSelector(
    entrySliceSelectors.selectEntriesTablePageSize,
  );

  const columns =
    tableSettings?.columns?.filter((c) => c.field !== "task") || [];

  const { t } = useTranslation();
  const {
    duplicateEntries: allEntries,
    duplicateEntryVisible,
    setDuplicateEntryVisible,
    setDuplicateEntries,
    setEntryUpdateVisible,
    currentOnIgnoreAction,
    setCurrentOnIgnoreAction,
    setDuplicateEntriesGroups,
  } = useDrawersContext();
  const dispatch = useAppDispatch();

  const { getValues: entriesFilterGetValues } =
    useFormContext<GetLatestEntriesFilterDto>();
  const { watch, reset, setValue, getValues } = useForm<ResolveFormValues>({
    defaultValues: {},
  });
  const finalEntry = watch("finalEntry");

  const activeEntries = allEntries.filter((e) => !excludedIds.includes(e._id));
  const otherEntries = activeEntries
    .filter((e) => e._id !== finalEntry?._id)
    .sort(byCreatedAt);
  const leftOutEntries = allEntries.filter((e) => excludedIds.includes(e._id));
  const originalFinalEntry = allEntries.find((e) => e._id === finalEntry?._id);

  const hasContact = activeEntries.some((e) => e.kind === EntryKind.CONTACT);
  const hasJunkOther = otherEntries.some((e) => e.kind === EntryKind.JUNK);
  const canExclude = otherEntries.length >= 2;
  const isKeptModified = Boolean(
    finalEntry &&
      originalFinalEntry &&
      (!isEqual(finalEntry.fields, originalFinalEntry.fields) ||
        (finalEntry.description || "") !==
          (originalFinalEntry.description || "")),
  );

  const descriptors = getDuplicateEntryFieldDescriptors(columns, {
    t,
    permissions: userPermissions,
    user,
    members,
  });

  const localOnClose = () => {
    setDuplicateEntryVisible(false);
    setDuplicateEntries([]);
    setExcludedIds([]);
    setPreviewId(null);
    setCurrentOnIgnoreAction(undefined);
  };

  const onUpdateFieldsInFinalEntry = (updatedField: Partial<Entry>) => {
    setValue("finalEntry", { ...finalEntry, ...updatedField } as Entry);
  };

  const onUpdate = ({
    field,
    value,
    mode,
  }: {
    field: string;
    value: string;
    mode: "append" | "set";
  }) => {
    const currentValue = getEntryFieldValue(finalEntry, field);

    setValue("finalEntry", {
      ...finalEntry,
      ...withEntryFieldValue(
        finalEntry,
        field,
        mode === "append" && currentValue ? `${currentValue}, ${value}` : value,
      ),
    });
  };

  const onExcludeEntry = (entryId: string) =>
    setExcludedIds((ids) => [...ids, entryId]);

  const onRestoreEntry = (entryId: string) =>
    setExcludedIds((ids) => ids.filter((id) => id !== entryId));

  const onMakeFinalEntry = (entryId: string) => {
    const newFinalEntry = allEntries.find((e) => e._id === entryId);

    if (newFinalEntry) {
      setValue("finalEntry", newFinalEntry);
    }
  };

  const onResetFinalEntry = () => {
    if (originalFinalEntry) {
      setValue("finalEntry", originalFinalEntry);
    }
  };

  const onIgnoreAndContinue = async () => {
    try {
      setIgnoreButtonLoading(true);

      await currentOnIgnoreAction?.();

      await dispatch(
        entryActions.getLatestEntries({
          userId,
          ...{
            ...omitBy(entriesFilterGetValues(), isNil),
            assigneeIds: entriesFilterGetValues("assigneeIds")?.filter(Boolean),
          },
          meta: {
            count: entriesPageSize,
            page: entriesPage - 1,
          },
        }),
      ).unwrap();
      localOnClose();
      setEntryUpdateVisible(false);
      AtiraToast.success(t("entries.update.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setIgnoreButtonLoading(false);
    }
  };

  const onSaveAndMerge = async () => {
    try {
      setMergeButtonLoading(true);
      await dispatch(
        entryActions.mergeEntries({
          userId,
          finalFields: finalEntry.fields!,
          finalDescription: finalEntry.description,
          targetEntryId: finalEntry._id,
          entriesIds: otherEntries.map((e) => e._id),
        }),
      ).unwrap();
      await dispatch(
        entryActions.getLatestEntries({
          userId,
          ...omitBy(entriesFilterGetValues(), isNil),
          meta: {
            count: entriesPageSize,
            page: entriesPage - 1,
          },
        }),
      ).unwrap();
      const result = await dispatch(
        entryActions.findPossibleDuplicates({ userId }),
      ).unwrap();
      setDuplicateEntriesGroups(result.groups);
      localOnClose();
      setEntryUpdateVisible(false);
      AtiraToast.success(t("entries.merge.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setMergeButtonLoading(false);
    }
  };

  useEffect(() => {
    if (duplicateEntryVisible) {
      const contacts = allEntries.filter((e) => e.kind === EntryKind.CONTACT);
      const [defaultFinalEntry] = [
        ...(contacts.length ? contacts : allEntries),
      ].sort(byCreatedAt);

      reset({
        finalEntry: defaultFinalEntry,
        userId,
        finalFields: {},
      });
      setExcludedIds([]);
      setAutoPicked(
        defaultFinalEntry
          ? {
              entryId: defaultFinalEntry._id,
              wasContact: contacts.length > 0,
            }
          : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateEntryVisible]);

  const autoPickedReason =
    autoPicked?.entryId === finalEntry?._id
      ? t(
          autoPicked?.wasContact
            ? "entries.duplicates.keepCard.autoPicked.contact"
            : "entries.duplicates.keepCard.autoPicked.any",
          { count: allEntries.length },
        )
      : undefined;

  return (
    <Drawer
      title={t("entries.duplicates.drawer.title")}
      onClose={localOnClose}
      open={duplicateEntryVisible}
      width={"90%"}
      zIndex={2002}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          overflow: "hidden",
        },
      }}
      extra={
        <Flex gap="m">
          {currentOnIgnoreAction ? (
            <Tooltip title={t("entries.duplicates.actions.ignore.tooltip")}>
              <Button
                backgroundColor="danger"
                margin="none"
                dataTestId={TestIDs.entries.duplicates.resolve.ignore}
                onClick={onIgnoreAndContinue}
                loading={ignoreButtonLoading}
              >
                {t("entries.duplicates.actions.ignore")}
              </Button>
            </Tooltip>
          ) : null}

          <Tooltip
            title={
              hasJunkOther
                ? t("entries.duplicates.actions.merge.junkTooltip")
                : t("entries.duplicates.actions.merge.tooltip")
            }
          >
            <TooltipHost>
              <Button
                margin="none"
                hover
                disabled={hasJunkOther}
                dataTestId={TestIDs.entries.duplicates.resolve.merge}
                onClick={onSaveAndMerge}
                loading={mergeButtonLoading}
              >
                {t("entries.duplicates.actions.merge")}
              </Button>
            </TooltipHost>
          </Tooltip>
        </Flex>
      }
    >
      <Intro>
        <Info info={t("entries.duplicates.summary")} />

        {finalEntry ? (
          <Flex alignItems="center" gap="m" flexWrap="wrap">
            <Text fontSize="m" fontWeight="bold">
              {t("entries.duplicates.others.title", {
                count: otherEntries.length,
              })}
            </Text>
            <Text fontSize="s" color="darkerSub">
              {t("entries.duplicates.others.subtitle")}
            </Text>
            <Tooltip
              title={
                <Flex flexDirection="column" gap="s">
                  <Text fontSize="xs" color="white">
                    {t("entries.duplicates.others.help.compare")}
                  </Text>
                  <Text fontSize="xs" color="white">
                    {t("entries.duplicates.others.help.actions")}
                  </Text>
                </Flex>
              }
            >
              <span>
                <AtiraIcon icon={faCircleInfo} color="darkerSub" />
              </span>
            </Tooltip>
          </Flex>
        ) : null}

        {leftOutEntries.length ? (
          <LeftOutBox>
            <Flex alignItems="baseline" gap="s" flexWrap="wrap">
              <Text fontSize="s" fontWeight="bold">
                {t("entries.duplicates.leftOut.title", {
                  count: leftOutEntries.length,
                })}
              </Text>
              <Text fontSize="xs" color="darkerSub">
                {t("entries.duplicates.leftOut.subtitle")}
              </Text>
            </Flex>

            {leftOutEntries.map((entry) => (
              <Flex
                key={entry._id}
                alignItems="center"
                justifyContent="space-between"
                gap="m"
              >
                <Flex alignItems="center" gap="s" minWidth={0}>
                  <EntryKindBadge kind={entry.kind} />
                  <TextWithTruncated length={40} fontSize="s">
                    {getEntryLabel(entry)}
                  </TextWithTruncated>
                </Flex>

                <GhostButton
                  fontSize="xs"
                  icon={faRotateLeft}
                  onClick={() => onRestoreEntry(entry._id)}
                >
                  {t("entries.duplicates.leftOut.restore")}
                </GhostButton>
              </Flex>
            ))}
          </LeftOutBox>
        ) : null}
      </Intro>

      {finalEntry ? (
        <>
          <Scroller>
            <CardsGrid>
              {otherEntries.map((entry) => (
                <EntryDuplicateCard
                  key={entry._id}
                  entry={entry}
                  finalEntry={finalEntry}
                  descriptors={descriptors}
                  canMakeFinal={entry.kind === EntryKind.CONTACT || !hasContact}
                  canExclude={canExclude}
                  onUpdate={onUpdate}
                  onExclude={onExcludeEntry}
                  onMakeFinal={onMakeFinalEntry}
                  onPreview={setPreviewId}
                />
              ))}
            </CardsGrid>
          </Scroller>

          <KeepDock>
            <EntryKeepCard
              entry={finalEntry}
              otherEntries={otherEntries}
              descriptors={descriptors}
              isModified={isKeptModified}
              autoPickedReason={autoPickedReason}
              onUpdate={onUpdateFieldsInFinalEntry}
              onReset={onResetFinalEntry}
              onPreview={setPreviewId}
            />
          </KeepDock>
        </>
      ) : null}

      <EntryDuplicatePreviewDrawer
        entry={allEntries.find((e) => e._id === previewId) || null}
        descriptors={descriptors}
        note={
          previewId === finalEntry?._id && isKeptModified
            ? t("entries.duplicates.preview.keptModifiedNote")
            : undefined
        }
        onClose={() => setPreviewId(null)}
      />
    </Drawer>
  );
};
